import { AddItemProps } from '@wix/design-system';
import React, { ReactElement, useEffect } from 'react';
import { FiltersMap } from '@wix/bex-core';
import { ToolbarCollectionContentAutoSizer } from '../ToolbarCollectionContentAutoSizer';
import { observer } from 'mobx-react-lite';
import { GridFoldersState } from '../../state';
import { ActionsCell } from '../ActionsCell';
import { SupportedCardGalleryItemProps } from './SupportedCardGalleryItemProps';
import { useGridSyncProps } from './useGridSyncProps';
import { getToolbarCollectionFeaturesInitializers } from '../../state/getToolbarCollectionFeaturesInitializers';
import { GridFoldersSectionsGroup } from './GridFoldersSectionsGroup';
import { GridFoldersPresetProps } from './GridFolders.types';
import { CardGridItemBaseProps } from './CardGridItemBaseProps';
import { CollectionCard } from '../CollectionCard';
import {
  ToolbarCollection,
  ToolbarCollectionBaseProps,
} from '../ToolbarCollection';
import { GridFoldersPlaceholderStates } from './GridFoldersPlaceholderStates';
import { PartialAllFilters } from '../../state/FoldersAndItemsCollectionsState';
import { minimalRequiredWdsRuntimeCheck } from '../../minimalRequiredWdsRuntimeCheck';

export interface GridFoldersBaseProps<
  T1,
  F1 extends FiltersMap,
  T2,
  F2 extends FiltersMap,
> extends Omit<
      ToolbarCollectionBaseProps<T1, PartialAllFilters<F1, F2>>,
      'renderEmptyState' | 'topNotification'
    >,
    CardGridItemBaseProps<T1, F1> {
  dataHook?: string;
  /** A GridFoldersState instance created using [useGridFolders](/pages/cairo/?path=/story/common-hooks--usegridfolders). */
  state: GridFoldersState<T1, F1, T2, F2>;
  /** Optional custom title for the items section */
  itemsSectionTitle?: string;
  foldersSectionTitle?: string;
  /** Same as `renderItem`, but for a folder item. */
  renderFolderItem?: (
    item: T2,
    index: number,
  ) => Partial<SupportedCardGalleryItemProps>;
  /** Same as `preset` but for a folder item */
  folderPreset?: 'full' | 'title';
  /** Same as `actionsCell`, but for a folder item. */
  folderActionsCell?: ActionsCell<T2, F2>;
  /** Same as `renderAddItem`, but for a folder item. */
  renderFolderAddItem?: () => ReactElement<AddItemProps>;

  renderEmptyState?: () => ReactElement;
  topNotification?: ReactElement;
}

export type GridFoldersBasePropsWithoutPreset<
  T1,
  F1 extends FiltersMap,
  T2,
  F2 extends FiltersMap,
> = Omit<
  GridFoldersBaseProps<T1, F1, T2, F2>,
  'preset' | 'folderPreset' | 'renderItem' | 'renderFolderItem'
>;

export type GridFoldersProps<
  T1,
  F1 extends FiltersMap,
  T2,
  F2 extends FiltersMap,
> = GridFoldersPresetProps<
  T1,
  T2,
  GridFoldersBasePropsWithoutPreset<T1, F1, T2, F2>
>;

function _GridFolders<T1, F1 extends FiltersMap, T2, F2 extends FiltersMap>(
  props: GridFoldersProps<T1, F1, T2, F2>,
) {
  const {
    state,
    exportModal,
    dataHook,
    cardProps,
    renderEmptyState,
    renderError,
    topNotification,
  } = props;

  const { toolbar } = state;

  if (process.env.NODE_ENV !== 'production') {
    // https://github.com/wix-private/wix-design-systems/pull/13091/files#diff-0befb17e7fa12b244a3efce19b067187b39d3a96d4f7e62a59f4f0fd876dc37cR102
    minimalRequiredWdsRuntimeCheck({
      required: '^1.89.0',
      requiredBy: 'Cairo/GridFolders',
    });
  }

  useGridSyncProps({ ...props, state: state.items });
  useGridSyncProps({
    ...props,
    renderItem: props.renderFolderItem,
    actionsCell: props.folderActionsCell,
    renderAddItem: props.renderFolderAddItem,
    state: state.folders,
    isUsingFolders: true,
  });

  useEffect(
    () =>
      state.init({
        featuresInitializers: getToolbarCollectionFeaturesInitializers(props),
      }),
    [],
  );

  return (
    <CollectionCard
      renderError={false}
      state={toolbar}
      dataHook={dataHook}
      {...cardProps}
    >
      <ToolbarCollection {...props} state={toolbar}>
        <GridFoldersPlaceholderStates
          state={state}
          renderEmptyState={renderEmptyState}
          renderError={renderError}
          topNotification={topNotification}
        >
          <ToolbarCollectionContentAutoSizer>
            <GridFoldersSectionsGroup {...props} />
          </ToolbarCollectionContentAutoSizer>
        </GridFoldersPlaceholderStates>
      </ToolbarCollection>
      {exportModal ?? toolbar.featuredComponents.exportModal.value}
    </CollectionCard>
  );
}

export const GridFolders = observer(_GridFolders);
